import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const ListingManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [businessName, setBusinessName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [error, setError] = useState(null);

  const handleAudit = async () => {
    if (!businessName) { setError('Please enter a business name'); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/local-seo/listing-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ businessName, address, phone, website })
      });
      const data = await res.json();
      if (data.success) { setAuditData(data.data); }
      else { setError(data.error || 'Audit failed'); }
    } catch (err) { setError('Network error'); }
    setLoading(false);
  };

  const cs = { background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '20px' };

  const scoreColor = (s) => s >= 80 ? '#28a745' : s >= 60 ? '#ffc107' : s >= 40 ? '#fd7e14' : '#dc3545';

  const tabBtn = (id, label) => (
    <button onClick={() => setActiveTab(id)} style={{
      padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px',
      borderBottom: activeTab === id ? '3px solid #1a73e8' : '3px solid transparent',
      color: activeTab === id ? '#1a73e8' : '#666', fontWeight: activeTab === id ? 600 : 400
    }}>{label}</button>
  );

  const statusBadge = (status) => {
    const colors = { accurate: '#28a745', inaccurate: '#dc3545', missing: '#999', found: '#1a73e8', 'not found': '#999' };
    return (<span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
      background: (colors[status] || '#999') + '18', color: colors[status] || '#999' }}>{status}</span>);
  };


  const LISTING_FAQ = [
    { q: "What is business listing management and why does it matter?", a: "Business listing management is the process of creating, claiming, and maintaining accurate business information across online directories, maps, and review sites. Consistent Name, Address, and Phone number (NAP) data across all listings is a critical local SEO ranking factor. Inconsistent or outdated listings confuse search engines and customers, reducing your local search visibility and credibility." },
    { q: "How does SERPfinity help manage business listings?", a: "SERPfinity scans hundreds of directories to find existing listings, identifies inconsistencies in your business information, and helps you correct them from a single dashboard. We monitor for unauthorized changes, track new citations, manage review responses, and ensure your business data remains accurate across Google Business Profile, Bing Places, Apple Maps, Yelp, Facebook, and industry-specific directories." },
    { q: "What is NAP consistency and why is it important for local SEO?", a: "NAP stands for Name, Address, and Phone number. NAP consistency means your business information is identical across every online directory, website, and social profile. Search engines cross-reference NAP data to verify business legitimacy and determine local rankings. Even minor differences like abbreviating Street vs St can create confusion. SERPfinity audits your NAP consistency across all major platforms." },
    { q: "How many business directories should I be listed in?", a: "The optimal number depends on your industry and location, but most businesses should maintain accurate listings in at least 40 to 60 core directories including Google Business Profile, Bing Places, Apple Maps, Yelp, Facebook, Yellow Pages, and industry-specific platforms. SERPfinity identifies the most impactful directories for your specific business category and geographic area." },
    { q: "Can listing management improve my Google Maps ranking?", a: "Yes, accurate and comprehensive business listings directly impact Google Maps rankings. Google uses citation signals including quantity, accuracy, and consistency of NAP data across the web as a local ranking factor. SERPfinity helps optimize your Google Business Profile and ensure supporting citations reinforce your location relevance, directly improving your Google Maps visibility." },
    { q: "How does listing management connect to AI search visibility?", a: "AI search assistants like ChatGPT, Google Gemini, and Siri pull business information from directory listings and knowledge graphs. Accurate, consistent listings increase the likelihood that AI platforms provide correct information about your business when users ask for recommendations. SERPfinity ensures your business data is optimized for both traditional local search and AI-powered discovery." }
  ];

  return (
    <>
      <Helmet>
        <title>Business Listing Management Tool | Local SEO Listings | SERPfinity</title>
        <meta name="description" content="Manage and optimize your business listings across all major directories. Ensure NAP consistency, monitor reviews, and boost local search visibility with SERPfinity listing management." />
        <meta name="keywords" content="business listing management, local SEO listings, NAP consistency, Google Business Profile, directory management, citation building, local search optimization, SERPfinity" />
        <link rel="canonical" href="https://thedigitalhall.club/listing-management" />
        <meta property="og:title" content="Business Listing Management | SERPfinity" />
        <meta property="og:description" content="Manage business listings across directories. Ensure NAP consistency and boost local search visibility." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thedigitalhall.club/listing-management" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Business Listing Management | SERPfinity" />
        <meta name="twitter:description" content="Comprehensive business listing management for local SEO. Manage citations and directory listings." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":LISTING_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <div style={{padding: '30px', maxWidth: '1200px', margin: '0 auto'}}>
      <div style={{marginBottom: '30px'}}>
        <h1 style={{fontSize: '28px', fontWeight: 700, color: '#1a1a2e', margin: 0}}>Listing Management</h1>
        <p style={{color: '#666', marginTop: '8px'}}>Audit NAP consistency and manage business listings across directories</p>
      </div>

      <div style={cs}>
        <h3 style={{margin: '0 0 16px', fontSize: '16px', color: '#333'}}>Business Information</h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px'}}>
          <div>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Business Name *</label>
            <input value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Your Business Name"
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'}} />
          </div>
          <div>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Address</label>
            <input value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St, Brooklyn, NY"
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'}} />
          </div>
          <div>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567"
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'}} />
          </div>
          <div>
            <label style={{display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px'}}>Website</label>
            <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="yourbusiness.com"
              style={{width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'}} />
          </div>
        </div>
        <button onClick={handleAudit} disabled={loading} style={{
          padding: '12px 28px', background: loading ? '#ccc' : '#1a73e8', color: '#fff', border: 'none',
          borderRadius: '8px', cursor: loading ? 'default' : 'pointer', fontWeight: 600, fontSize: '14px'
        }}>{loading ? 'Auditing...' : 'Run Listing Audit'}</button>
        {error && <div style={{color: '#dc3545', marginTop: '12px', fontSize: '14px'}}>{error}</div>}
      </div>

      {auditData && (
        <>
          <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px'}}>
            <div style={{...cs, flex: 1, minWidth: '180px', textAlign: 'center'}}>
              <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>NAP Score</div>
              <div style={{fontSize: '36px', fontWeight: 700, color: scoreColor(auditData.napScore)}}>{auditData.napScore}%</div>
              <div style={{fontSize: '12px', color: '#999'}}>Consistency</div>
            </div>
            <div style={{...cs, flex: 1, minWidth: '180px', textAlign: 'center'}}>
              <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>Listings Found</div>
              <div style={{fontSize: '36px', fontWeight: 700, color: '#1a73e8'}}>{auditData.totalListings}</div>
              <div style={{fontSize: '12px', color: '#999'}}>Across directories</div>
            </div>
            <div style={{...cs, flex: 1, minWidth: '180px', textAlign: 'center'}}>
              <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>Accurate</div>
              <div style={{fontSize: '36px', fontWeight: 700, color: '#28a745'}}>{auditData.accurateCount}</div>
              <div style={{fontSize: '12px', color: '#999'}}>Matching NAP</div>
            </div>
            <div style={{...cs, flex: 1, minWidth: '180px', textAlign: 'center'}}>
              <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>Issues</div>
              <div style={{fontSize: '36px', fontWeight: 700, color: '#dc3545'}}>{auditData.issueCount}</div>
              <div style={{fontSize: '12px', color: '#999'}}>Need fixing</div>
            </div>
          </div>

          <div style={{...cs, padding: 0, overflow: 'hidden'}}>
            <div style={{display: 'flex', borderBottom: '1px solid #eee', padding: '0 16px'}}>
              {tabBtn('overview', 'Overview')}
              {tabBtn('directories', 'Directory Listings')}
              {tabBtn('nap', 'NAP Audit')}
              {tabBtn('citations', 'Citation Sources')}
              {tabBtn('actions', 'Action Items')}
            </div>
            <div style={{padding: '24px'}}>

              {activeTab === 'overview' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Listing Health Overview</h3>
                  <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '24px'}}>
                    <div style={{flex: 1, minWidth: '250px'}}>
                      <div style={{background: '#f8f9fa', borderRadius: '8px', padding: '20px'}}>
                        <div style={{fontSize: '14px', fontWeight: 600, marginBottom: '12px'}}>NAP Consistency Breakdown</div>
                        {['Name', 'Address', 'Phone', 'Website'].map((field, i) => {
                          const val = auditData.napBreakdown[field.toLowerCase()] || 0;
                          return (
                            <div key={i} style={{marginBottom: '10px'}}>
                              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px'}}>
                                <span>{field}</span><span style={{fontWeight: 600, color: scoreColor(val)}}>{val}%</span>
                              </div>
                              <div style={{height: '8px', background: '#e9ecef', borderRadius: '4px'}}>
                                <div style={{width: val + '%', height: '100%', background: scoreColor(val), borderRadius: '4px', transition: 'width 0.5s'}}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{flex: 1, minWidth: '250px'}}>
                      <div style={{background: '#f8f9fa', borderRadius: '8px', padding: '20px'}}>
                        <div style={{fontSize: '14px', fontWeight: 600, marginBottom: '12px'}}>Directory Coverage</div>
                        {[
                          {label: 'Major Directories', count: auditData.coverage.major, total: auditData.coverage.majorTotal},
                          {label: 'Social Platforms', count: auditData.coverage.social, total: auditData.coverage.socialTotal},
                          {label: 'Industry-Specific', count: auditData.coverage.industry, total: auditData.coverage.industryTotal},
                          {label: 'Local Directories', count: auditData.coverage.local, total: auditData.coverage.localTotal}
                        ].map((item, i) => (
                          <div key={i} style={{display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 3 ? '1px solid #e9ecef' : 'none'}}>
                            <span style={{fontSize: '13px'}}>{item.label}</span>
                            <span style={{fontSize: '13px', fontWeight: 600}}>{item.count}/{item.total}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'directories' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Directory Listings</h3>
                  <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px'}}>
                      <thead>
                        <tr style={{background: '#f8f9fa'}}>
                          <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>Directory</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Status</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Name</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Address</th>
                          <th style={{padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Phone</th>
                          <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.listings.map((l, i) => (
                          <tr key={i} style={{borderBottom: '1px solid #eee'}}>
                            <td style={{padding: '12px', fontWeight: 500}}>{l.directory}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>{statusBadge(l.status)}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>{l.nameMatch ? '\u2705' : '\u274C'}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>{l.addressMatch ? '\u2705' : '\u274C'}</td>
                            <td style={{padding: '12px', textAlign: 'center'}}>{l.phoneMatch ? '\u2705' : '\u274C'}</td>
                            <td style={{padding: '12px'}}>{l.url ? <a href={l.url} target="_blank" rel="noopener noreferrer" style={{color: '#1a73e8', textDecoration: 'none'}}>View</a> : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'nap' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>NAP Consistency Audit</h3>
                  <div style={{background: '#f0f7ff', padding: '16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px'}}>
                    <strong>Your NAP:</strong> {businessName} | {address || 'N/A'} | {phone || 'N/A'} | {website || 'N/A'}
                  </div>
                  {auditData.napIssues.map((issue, i) => (
                    <div key={i} style={{padding: '16px', background: '#fff8f8', border: '1px solid #ffe0e0', borderRadius: '8px', marginBottom: '12px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                        <span style={{fontWeight: 600}}>{issue.directory}</span>
                        <span style={{fontSize: '12px', padding: '3px 8px', borderRadius: '4px',
                          background: issue.severity === 'high' ? '#dc354520' : '#ffc10720',
                          color: issue.severity === 'high' ? '#dc3545' : '#e6a200'}}>{issue.severity}</span>
                      </div>
                      <div style={{fontSize: '13px', color: '#666'}}>
                        <div><strong>Field:</strong> {issue.field}</div>
                        <div><strong>Expected:</strong> {issue.expected}</div>
                        <div><strong>Found:</strong> <span style={{color: '#dc3545'}}>{issue.found}</span></div>
                      </div>
                    </div>
                  ))}
                  {auditData.napIssues.length === 0 && (
                    <div style={{textAlign: 'center', padding: '40px', color: '#28a745', fontSize: '16px'}}>
                      All listings have consistent NAP information!
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'citations' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Citation Sources</h3>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px'}}>
                    {auditData.citations.map((c, i) => (
                      <div key={i} style={{padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                          <span style={{fontWeight: 600, fontSize: '15px'}}>{c.source}</span>
                          <span style={{fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                            background: c.type === 'structured' ? '#1a73e820' : '#6f42c120',
                            color: c.type === 'structured' ? '#1a73e8' : '#6f42c1'}}>{c.type}</span>
                        </div>
                        <div style={{fontSize: '13px', color: '#666', marginBottom: '4px'}}>{c.category}</div>
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999'}}>
                          <span>DA: {c.domainAuthority}</span>
                          <span>{statusBadge(c.status)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'actions' && (
                <div>
                  <h3 style={{margin: '0 0 16px', fontSize: '16px'}}>Action Items</h3>
                  {auditData.actions.map((a, i) => (
                    <div key={i} style={{display: 'flex', gap: '16px', padding: '16px', background: i % 2 === 0 ? '#f8f9fa' : '#fff', borderRadius: '8px', marginBottom: '8px', alignItems: 'center'}}>
                      <div style={{width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px',
                        background: a.priority === 'high' ? '#dc354520' : a.priority === 'medium' ? '#ffc10720' : '#28a74520',
                        color: a.priority === 'high' ? '#dc3545' : a.priority === 'medium' ? '#e6a200' : '#28a745'}}>{i + 1}</div>
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 500, fontSize: '14px'}}>{a.title}</div>
                        <div style={{fontSize: '13px', color: '#666', marginTop: '2px'}}>{a.description}</div>
                      </div>
                      <span style={{fontSize: '12px', padding: '3px 10px', borderRadius: '12px',
                        background: a.priority === 'high' ? '#dc354518' : a.priority === 'medium' ? '#ffc10718' : '#28a74518',
                        color: a.priority === 'high' ? '#dc3545' : a.priority === 'medium' ? '#e6a200' : '#28a745'}}>{a.priority}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!auditData && !loading && (
        <div style={{...cs, textAlign: 'center', padding: '60px 30px'}}>
          <div style={{fontSize: '48px', marginBottom: '16px'}}>\uD83D\uDCCB</div>
          <h3 style={{margin: '0 0 8px', color: '#333'}}>Audit Your Business Listings</h3>
          <p style={{color: '#666', maxWidth: '500px', margin: '0 auto'}}>
            Enter your business information to check NAP consistency across major directories, find missing listings, and get actionable fixes.
          </p>
        </div>
      )}
    </div>
      <FAQSection items={LISTING_FAQ} />
    </>
  );
};

export default ListingManagement;
